import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage } from '../../../../channels/chat/components/sharedChatService';
import {
    appendUnderSlot,
    buildPhaseDocumentMarkdown,
    downloadMarkdownFile,
} from './phaseDocumentModel';
import type { PhaseDocumentsState } from './phaseDocumentModel';

interface LiveDiscoveryDocumentProps {
    projectName: string;
    projectId?: number | string | null;
    chatMessages: ChatMessage[];
}

const getProjectSlug = (projectName: string) =>
    projectName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'untitled-project';

const getPhaseFileSlug = (phaseTitle: string) =>
    phaseTitle.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'phase';

/** Single source-of-truth localStorage key for a project's live discovery document. */
export function getDiscoveryDocumentStorageKey(
    projectId: number | string | null | undefined,
    _projectName?: string
): string {
    const hasId = projectId !== undefined && projectId !== null && String(projectId).trim() !== '';
    return hasId ? `live_doc_${String(projectId)}` : 'live_doc_draft';
}

function hasStableDiscoveryProjectId(projectId: number | string | null | undefined): boolean {
    return projectId !== undefined && projectId !== null && String(projectId).trim() !== '';
}

type LiveDocSlot = {
    slot_no: number;
    slot: string;
    slot_value: string;
};

type LiveDocPhase = {
    phase: string;
    slots: LiveDocSlot[];
};

type LiveDocPayload = {
    project_id: string;
    phases: LiveDocPhase[];
    updated_at?: string;
};

function sortByTime(a: ChatMessage, b: ChatMessage): number {
    const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp as string).getTime();
    const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp as string).getTime();
    return ta - tb;
}

function applyDiscoveryMessageToLiveDoc(payload: LiveDocPayload, message: ChatMessage): LiveDocPayload {
    if (message.channel !== 'discovery' || message.type !== 'assistant') return payload;
    const mt = message.msgType ?? message.discovery?.msg_type;

    if (mt === 'index' && message.discovery?.index_value?.length) {
        const nextPhases = message.discovery.index_value
            .map((row) => {
                const phaseName = String(row.phase ?? '').trim();
                if (!phaseName) return null;
                const priorPhase = payload.phases.find((p) => p.phase === phaseName);
                const slots = (row.slots ?? [])
                    .map((slot, idx) => ({
                        slot: String(slot ?? '').trim(),
                        slot_no: idx + 1,
                    }))
                    .filter((s) => s.slot.length > 0)
                    .map((slotRow) => {
                        const previousSlot = priorPhase?.slots.find((s) => s.slot === slotRow.slot);
                        return {
                            slot_no: slotRow.slot_no,
                            slot: slotRow.slot,
                            slot_value: previousSlot?.slot_value ?? '',
                        };
                    });
                return { phase: phaseName, slots };
            })
            .filter((p): p is LiveDocPhase => p !== null);

        return {
            project_id: payload.project_id,
            phases: nextPhases,
            updated_at: new Date().toISOString(),
        };
    }

    if (mt === 'doc_summary' && message.discovery?.phase && message.discovery?.slot) {
        const phaseName = String(message.discovery.phase).trim();
        const slotName = String(message.discovery.slot).trim();
        const text = String(message.content ?? '').trim();
        if (!phaseName || !slotName || !text) return payload;

        const nextPayload: LiveDocPayload = {
            project_id: payload.project_id,
            phases: payload.phases.map((p) => ({ ...p, slots: p.slots.map((s) => ({ ...s })) })),
            updated_at: new Date().toISOString(),
        };

        let phase = nextPayload.phases.find((p) => p.phase === phaseName);
        if (!phase) {
            phase = { phase: phaseName, slots: [] };
            nextPayload.phases.push(phase);
        }

        let slot = phase.slots.find((s) => s.slot === slotName);
        if (!slot) {
            slot = {
                slot_no: phase.slots.length + 1,
                slot: slotName,
                slot_value: '',
            };
            phase.slots.push(slot);
        }
        slot.slot_value = slot.slot_value.trim().length > 0 ? `${slot.slot_value}\n\n${text}` : text;
        return nextPayload;
    }

    return payload;
}

function liveDocPayloadToPhaseState(payload: LiveDocPayload): PhaseDocumentsState {
    const phaseOrder: string[] = [];
    const phases: Record<string, string> = {};

    for (const p of payload.phases) {
        const phaseName = String(p.phase ?? '').trim();
        if (!phaseName) continue;
        phaseOrder.push(phaseName);
        const slotLabels = p.slots.map((s) => String(s.slot ?? '').trim()).filter(Boolean);
        let markdown = buildPhaseDocumentMarkdown(phaseName, slotLabels);
        for (const s of p.slots) {
            const slotName = String(s.slot ?? '').trim();
            const slotValue = String(s.slot_value ?? '');
            if (!slotName) continue;
            markdown = appendUnderSlot(markdown, slotName, slotValue);
        }
        phases[phaseName] = markdown;
    }

    return { phases, phaseOrder };
}

function loadLocalPhaseDocuments(projectId: number | string): PhaseDocumentsState {
    if (typeof window === 'undefined') {
        return { phases: {}, phaseOrder: [] };
    }
    const storageKey = getDiscoveryDocumentStorageKey(projectId);
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return { phases: {}, phaseOrder: [] };
        const parsed = JSON.parse(raw) as LiveDocPayload;
        if (!Array.isArray(parsed?.phases)) return { phases: {}, phaseOrder: [] };
        return liveDocPayloadToPhaseState(parsed);
    } catch {
        return { phases: {}, phaseOrder: [] };
    }
}

function loadLiveDocPayload(projectId: number | string): LiveDocPayload | null {
    if (typeof window === 'undefined') return null;
    const storageKey = getDiscoveryDocumentStorageKey(projectId);
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as LiveDocPayload;
        if (!Array.isArray(parsed?.phases)) return null;
        return {
            project_id: String(parsed.project_id ?? projectId),
            phases: parsed.phases.map((p) => ({
                phase: String(p.phase ?? '').trim(),
                slots: Array.isArray(p.slots)
                    ? p.slots.map((s, idx) => ({
                          slot_no: Number(s.slot_no ?? idx + 1),
                          slot: String(s.slot ?? '').trim(),
                          slot_value: String(s.slot_value ?? ''),
                      }))
                    : [],
            })),
            updated_at: parsed.updated_at,
        };
    } catch {
        return null;
    }
}

function persistLocalPhaseDocuments(projectId: number | string, payload: LiveDocPayload): void {
    if (typeof window === 'undefined') return;
    const storageKey = getDiscoveryDocumentStorageKey(projectId);
    localStorage.setItem(storageKey, JSON.stringify(payload));
}

function phaseMarkdownToSlots(markdown: string, fallbackSlots: LiveDocSlot[]): LiveDocSlot[] {
    const text = String(markdown ?? '');
    const headingRegex = /^###\s+(.+)$/gm;
    const matches = [...text.matchAll(headingRegex)];
    if (matches.length === 0) {
        return fallbackSlots;
    }

    const extracted: Array<{ slot: string; slot_value: string }> = [];
    for (let i = 0; i < matches.length; i++) {
        const current = matches[i];
        const next = matches[i + 1];
        const slot = String(current[1] ?? '').trim();
        if (!slot) continue;
        const start = current.index! + current[0].length;
        const end = next ? next.index! : text.length;
        const slotValue = text.slice(start, end).trim();
        extracted.push({ slot, slot_value: slotValue });
    }

    return extracted.map((s, idx) => {
        const existing = fallbackSlots.find((f) => f.slot === s.slot);
        return {
            slot_no: existing?.slot_no ?? idx + 1,
            slot: s.slot,
            slot_value: s.slot_value,
        };
    });
}

const TOC_HEADING_MARKER = '<<<VISH_DISCOVERY_TOC_HEADING>>>';

/** Consecutive `1. foo` lines → single `<ol>` so TOC is not merged with body lists. */
const convertConsecutiveNumberedLines = (text: string): string => {
    const lines = text.split('\n');
    const out: string[] = [];
    let i = 0;
    while (i < lines.length) {
        const m = lines[i].match(/^(\d+)\.\s+(.*)$/);
        if (m) {
            const items: string[] = [];
            while (i < lines.length) {
                const mm = lines[i].match(/^(\d+)\.\s+(.*)$/);
                if (!mm) break;
                items.push(`<li>${mm[2]}</li>`);
                i++;
            }
            out.push(`<ol class="discovery-toc-ol">${items.join('')}</ol>`);
            continue;
        }
        out.push(lines[i]);
        i++;
    }
    return out.join('\n');
};

/** Consecutive `- item` lines → single `<ul>` (body lists, not one mega-list with TOC). */
const convertConsecutiveBulletLines = (text: string): string => {
    const lines = text.split('\n');
    const out: string[] = [];
    let i = 0;
    while (i < lines.length) {
        const m = lines[i].match(/^-\s+(.*)$/);
        if (m) {
            const items: string[] = [];
            while (i < lines.length) {
                const mm = lines[i].match(/^-\s+(.*)$/);
                if (!mm) break;
                items.push(`<li>${mm[1]}</li>`);
                i++;
            }
            out.push(`<ul class="discovery-body-ul">${items.join('')}</ul>`);
            continue;
        }
        out.push(lines[i]);
        i++;
    }
    return out.join('\n');
};

const renderMarkdownToHtml = (markdown: string): string => {
    let html = markdown.trimEnd();

    html = convertConsecutiveNumberedLines(html);
    html = convertConsecutiveBulletLines(html);

    html = html.replace(/^##\s+Table of Contents\s*$/gm, TOC_HEADING_MARKER);

    html = html.replace(/^###\s+(.*)$/gm, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.*)$/gm, '<h2 class="discovery-slot-heading">$1</h2>');
    let firstAtxHeading = true;
    html = html.replace(/^#\s+(.*)$/gm, (_, title) => {
        if (firstAtxHeading) {
            firstAtxHeading = false;
            return `<h1 class="discovery-phase-title">${title}</h1>`;
        }
        return `<h1 class="discovery-phase-body-h1">${title}</h1>`;
    });
    html = html.replace(new RegExp(TOC_HEADING_MARKER, 'g'), '<h2 class="discovery-toc-label">Table of Contents</h2>');

    html = html.replace(/^---\s*$/gm, '<hr>');

    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    html = html.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');
    html = `<p>${html}</p>`;
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<h[1-3])/g, '$1');
    html = html.replace(/(<\/h[1-3]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<hr>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ul)/g, '$1');
    html = html.replace(/(<\/ul>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ol)/g, '$1');
    html = html.replace(/(<\/ol>)<\/p>/g, '$1');

    return html;
};

const htmlToMarkdown = (html: string): string => {
    if (typeof window === 'undefined') return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const normalizeInline = (text: string) =>
        text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();

    const parseNode = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent || '';
        }
        if (!(node instanceof HTMLElement)) {
            return '';
        }

        const tag = node.tagName.toLowerCase();
        const childrenText = Array.from(node.childNodes).map(parseNode).join('');

        if (tag === 'strong' || tag === 'b') return `**${normalizeInline(childrenText)}**`;
        if (tag === 'em' || tag === 'i') return `*${normalizeInline(childrenText)}*`;
        if (tag === 'code') return `\`${normalizeInline(childrenText)}\``;
        if (tag === 'a') return `[${normalizeInline(childrenText)}](${node.getAttribute('href') || '#'})`;
        if (tag === 'h1') return `# ${normalizeInline(childrenText)}\n\n`;
        if (tag === 'h2') {
            if (node.classList.contains('discovery-toc-label')) {
                return `## Table of Contents\n\n`;
            }
            return `## ${normalizeInline(childrenText)}\n\n`;
        }
        if (tag === 'h3') return `### ${normalizeInline(childrenText)}\n\n`;
        if (tag === 'ul') {
            const lis = Array.from(node.querySelectorAll(':scope > li'));
            return (
                lis
                    .map((li) => {
                        const inner = Array.from(li.childNodes).map(parseNode).join('').trim();
                        return `- ${normalizeInline(inner)}\n`;
                    })
                    .join('') + '\n'
            );
        }
        if (tag === 'ol') {
            const lis = Array.from(node.querySelectorAll(':scope > li'));
            return (
                lis
                    .map((li, idx) => {
                        const inner = Array.from(li.childNodes).map(parseNode).join('').trim();
                        return `${idx + 1}. ${normalizeInline(inner)}\n`;
                    })
                    .join('') + '\n'
            );
        }
        if (tag === 'li') return `- ${normalizeInline(childrenText)}\n`;
        if (tag === 'hr') return '---\n\n';
        if (tag === 'br') return '\n';
        if (tag === 'p' || tag === 'div') {
            const text = childrenText.trim();
            return text ? `${text}\n\n` : '';
        }
        return childrenText;
    };

    const markdown = Array.from(doc.body.childNodes).map(parseNode).join('');
    return markdown.replace(/\n{3,}/g, '\n\n').trim();
};

function mergePhaseOrder(state: PhaseDocumentsState): string[] {
    const { phases, phaseOrder } = state;
    if (phaseOrder.length > 0) {
        return [...phaseOrder, ...Object.keys(phases).filter((k) => !phaseOrder.includes(k))];
    }
    return Object.keys(phases);
}

const LiveDiscoveryDocument: React.FC<LiveDiscoveryDocumentProps> = ({
    projectName,
    projectId,
    chatMessages,
}) => {
    const stableProjectId = useMemo(
        () => (hasStableDiscoveryProjectId(projectId) ? String(projectId) : null),
        [projectId]
    );

    const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
    const [editableHtml, setEditableHtml] = useState('');
    const [documentMode, setDocumentMode] = useState<'preview' | 'edit'>('preview');
    const [showInfo, setShowInfo] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
    const [localPhaseState, setLocalPhaseState] = useState<PhaseDocumentsState>({ phases: {}, phaseOrder: [] });
    const processedDiscoveryMessageIdsRef = useRef<Set<string>>(new Set());
    const discoveryMessageCursorInitializedRef = useRef(false);

    useEffect(() => {
        if (!stableProjectId) {
            setLocalPhaseState({ phases: {}, phaseOrder: [] });
            processedDiscoveryMessageIdsRef.current = new Set();
            discoveryMessageCursorInitializedRef.current = false;
            return;
        }
        setLocalPhaseState(loadLocalPhaseDocuments(stableProjectId));
        processedDiscoveryMessageIdsRef.current = new Set();
        discoveryMessageCursorInitializedRef.current = false;
    }, [stableProjectId]);

    /**
     * Source of truth: localStorage. We DO NOT replay full history.
     * We only apply new/unseen discovery messages to existing `live_doc_{projectId}` JSON.
     */
    useEffect(() => {
        if (!stableProjectId) return;
        const ordered = [...chatMessages].sort(sortByTime);
        const discoveryRows = ordered.filter((m) => m.channel === 'discovery' && m.type === 'assistant');

        // Establish a cursor baseline for already-loaded history; prevents full replay rebuild.
        if (!discoveryMessageCursorInitializedRef.current) {
            processedDiscoveryMessageIdsRef.current = new Set(discoveryRows.map((m) => m.id));
            discoveryMessageCursorInitializedRef.current = true;
            return;
        }

        let payload =
            loadLiveDocPayload(stableProjectId) ?? {
                project_id: stableProjectId,
                phases: [],
            };
        let changed = false;
        const seen = processedDiscoveryMessageIdsRef.current;

        for (const msg of discoveryRows) {
            if (seen.has(msg.id)) continue;
            seen.add(msg.id);
            const nextPayload = applyDiscoveryMessageToLiveDoc(payload, msg);
            if (nextPayload !== payload) {
                payload = nextPayload;
                changed = true;
            }
        }

        if (!changed) return;
        try {
            persistLocalPhaseDocuments(stableProjectId, payload);
            setLocalPhaseState(liveDocPayloadToPhaseState(payload));
            setLastSavedAt(new Date().toISOString());
        } catch {
            /* ignore */
        }
    }, [stableProjectId, chatMessages]);

    const orderedPhaseKeys = useMemo(() => {
        const keys = mergePhaseOrder(localPhaseState);
        return keys.filter((k) => localPhaseState.phases[k]);
    }, [localPhaseState]);

    const isMultiPhase = orderedPhaseKeys.length > 0;

    const activePhase = useMemo(() => {
        if (!isMultiPhase) return null;
        if (selectedPhase && orderedPhaseKeys.includes(selectedPhase)) return selectedPhase;
        return orderedPhaseKeys[0] ?? null;
    }, [isMultiPhase, selectedPhase, orderedPhaseKeys]);

    useEffect(() => {
        if (orderedPhaseKeys.length === 0) {
            setSelectedPhase(null);
            return;
        }
        setSelectedPhase((prev) => (prev && orderedPhaseKeys.includes(prev) ? prev : orderedPhaseKeys[0]));
    }, [orderedPhaseKeys]);

    const currentPhaseMarkdown = useMemo(() => {
        if (!isMultiPhase || !activePhase) return '';
        return localPhaseState.phases[activePhase] ?? '';
    }, [isMultiPhase, activePhase, localPhaseState.phases]);

    useEffect(() => {
        if (documentMode === 'preview') return;
        if (isMultiPhase && activePhase) {
            setEditableHtml(renderMarkdownToHtml(currentPhaseMarkdown));
        }
    }, [documentMode, isMultiPhase, activePhase, currentPhaseMarkdown]);

    const handleDownloadMarkdown = useCallback(() => {
        const slug = getProjectSlug(projectName);
        if (isMultiPhase && activePhase) {
            const md = localPhaseState.phases[activePhase] ?? '';
            downloadMarkdownFile(`${slug}-${getPhaseFileSlug(activePhase)}`, md);
        }
    }, [
        isMultiPhase,
        activePhase,
        localPhaseState.phases,
        projectName,
    ]);

    const hasStoredPhaseDocuments = orderedPhaseKeys.length > 0;

    const hasActiveDocument = hasStoredPhaseDocuments;

    const showNoActiveDocuments = documentMode === 'preview' && !hasActiveDocument;

    const downloadFileLabel = isMultiPhase && activePhase
        ? `${getProjectSlug(projectName)}-${getPhaseFileSlug(activePhase)}.md`
        : `${getProjectSlug(projectName)}-phase.md`;

    return (
        <div className="relative h-full min-h-0 overflow-hidden bg-gradient-to-b from-indigo-50/40 via-white to-white">
            {hasActiveDocument && (
                <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={handleDownloadMarkdown}
                        className="h-9 w-9 rounded-xl bg-white/80 text-slate-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur-md hover:bg-white hover:text-slate-800 flex items-center justify-center transition-all"
                        title="Download current document as .md"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowInfo((prev) => !prev)}
                        className="h-9 w-9 rounded-xl bg-white/80 text-slate-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur-md hover:bg-white hover:text-slate-800 flex items-center justify-center transition-all"
                        title="Document info"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </button>
                </div>
            )}

            {showInfo && hasActiveDocument && (
                <div className="absolute top-14 left-3 z-10 w-72 rounded-2xl bg-white/85 p-3 text-xs text-slate-600 shadow-xl ring-1 ring-slate-200/70 backdrop-blur-md space-y-1.5">
                    <div><span className="font-semibold text-slate-800">File:</span> {downloadFileLabel}</div>
                    {isMultiPhase && activePhase && (
                        <div><span className="font-semibold text-slate-800">Phase:</span> {activePhase}</div>
                    )}
                    <div><span className="font-semibold text-slate-800">Last saved:</span> {lastSavedAt ? new Date(lastSavedAt).toLocaleString() : 'Not saved yet'}</div>
                    <div><span className="font-semibold text-slate-800">Project:</span> {projectName || 'Untitled Project'}</div>
                    <div><span className="font-semibold text-slate-800">Mode:</span> {documentMode === 'edit' ? 'Editing' : 'Preview'}</div>
                </div>
            )}

            {isMultiPhase && documentMode === 'preview' && hasActiveDocument && (
                <div className="absolute top-2.5 left-1/2 z-10 flex max-w-[min(100%-6rem,42rem)] -translate-x-1/2 items-center justify-center overflow-x-auto px-2 pb-1 [scrollbar-width:thin]">
                    <div
                        className="inline-flex items-center gap-0.5 rounded-2xl bg-white/80 p-1 shadow-sm ring-1 ring-slate-200/70 backdrop-blur-md"
                        role="tablist"
                        aria-label="Discovery phase documents"
                    >
                        {orderedPhaseKeys.map((key) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={activePhase === key}
                                onClick={() => setSelectedPhase(key)}
                                className={`shrink-0 rounded-[10px] px-5 py-2.5 text-sm font-medium tracking-tight transition-all duration-200 ease-out sm:px-6 sm:py-3 sm:text-[0.9375rem] ${
                                    activePhase === key
                                        ? 'bg-white font-semibold text-indigo-600 shadow-sm ring-1 ring-indigo-200/70'
                                        : 'bg-transparent text-slate-600 hover:bg-white/70 hover:text-slate-800'
                                }`}
                            >
                                {key}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {isMultiPhase && documentMode === 'edit' && (
                <div className="absolute top-[3.25rem] left-1/2 z-10 flex max-w-[min(100%-1.5rem,44rem)] -translate-x-1/2 flex-col items-center gap-2 px-2 sm:flex-row sm:flex-wrap sm:justify-center">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Editing
                    </span>
                    <div
                        className="inline-flex flex-wrap items-center justify-center gap-0.5 rounded-2xl bg-white/80 p-1 shadow-sm ring-1 ring-slate-200/70 backdrop-blur-md"
                        role="tablist"
                        aria-label="Select phase to edit"
                    >
                        {orderedPhaseKeys.map((key) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={activePhase === key}
                                onClick={() => setSelectedPhase(key)}
                                className={`shrink-0 rounded-[10px] px-4 py-2 text-xs font-medium tracking-tight transition-all duration-200 ease-out sm:px-5 sm:py-2.5 sm:text-sm ${
                                    activePhase === key
                                        ? 'bg-white font-semibold text-indigo-600 shadow-sm ring-1 ring-indigo-200/70'
                                        : 'bg-transparent text-slate-600 hover:bg-white/70 hover:text-slate-800'
                                }`}
                            >
                                {key}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="h-full min-h-0 overflow-y-auto overscroll-contain">
                {documentMode === 'edit' ? (
                    isMultiPhase && activePhase ? (
                        <div
                            key={`edit-phase-${activePhase}`}
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => {
                                const html = (e.currentTarget as HTMLDivElement).innerHTML;
                                setEditableHtml(html);
                                const markdown = htmlToMarkdown(html);
                                if (!stableProjectId || !activePhase) return;
                                setLocalPhaseState((prev) => {
                                    const payload =
                                        loadLiveDocPayload(stableProjectId) ?? {
                                            project_id: stableProjectId,
                                            phases: [],
                                        };
                                    const existingPhase = payload.phases.find((p) => p.phase === activePhase);
                                    if (!existingPhase) {
                                        payload.phases.push({
                                            phase: activePhase,
                                            slots: [],
                                        });
                                    }
                                    const phaseRef = payload.phases.find((p) => p.phase === activePhase)!;
                                    phaseRef.slots = phaseMarkdownToSlots(markdown, phaseRef.slots);
                                    payload.updated_at = new Date().toISOString();
                                    persistLocalPhaseDocuments(stableProjectId, payload);
                                    setLastSavedAt(payload.updated_at);
                                    return liveDocPayloadToPhaseState(payload);
                                });
                            }}
                            className={`discovery-doc-editor mx-3 my-14 min-h-[calc(100%-7rem)] w-auto rounded-2xl bg-white px-6 py-10 text-[15px] leading-[1.75] text-slate-800 ring-1 ring-slate-200/70 focus:outline-none focus:ring-2 focus:ring-indigo-200/80 md:mx-6 md:px-10 md:py-12${isMultiPhase ? ' discovery-suppress-phase-banner' : ''}`}
                            dangerouslySetInnerHTML={{ __html: editableHtml }}
                        />
                    ) : (
                        <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
                            <div className="text-center max-w-md">
                                <p className="text-sm leading-relaxed text-slate-600">
                                    No phase document available to edit yet.
                                </p>
                            </div>
                        </div>
                    )
                ) : showNoActiveDocuments ? (
                    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
                        <div className="text-center max-w-md">
                            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                Live document
                            </p>
                            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                                No documents present.
                            </h2>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600">
                                Nothing is currently stored for this project.
                            </p>
                        </div>
                    </div>
                ) : isMultiPhase && activePhase ? (
                    <article className="flex min-h-full px-3 py-14 pb-20 md:px-6 md:py-16">
                        <div
                            className="live-phase-markdown discovery-suppress-phase-banner flex-1 rounded-2xl bg-white px-7 py-9 text-[17px] leading-[1.8] text-slate-800 ring-1 ring-slate-200/70 md:px-10 md:py-11"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(currentPhaseMarkdown) }}
                        />
                    </article>
                ) : (
                    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
                        <div className="text-center max-w-md">
                            <p className="text-sm leading-relaxed text-slate-600">
                                No phase document available yet.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={() => setDocumentMode((prev) => (prev === 'edit' ? 'preview' : 'edit'))}
                className="absolute top-3 right-3 z-10 h-10 w-10 rounded-xl bg-indigo-600 text-white shadow-lg shadow-indigo-300/40 ring-1 ring-indigo-300/60 hover:bg-indigo-700 flex items-center justify-center transition-all"
                title={documentMode === 'edit' ? 'Switch to preview' : 'Edit document'}
                aria-label={documentMode === 'edit' ? 'Switch to preview' : 'Edit document'}
            >
                {documentMode === 'edit' ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                )}
            </button>

            <style>{`
                .live-phase-markdown.discovery-suppress-phase-banner h1.discovery-phase-title,
                .discovery-doc-editor.discovery-suppress-phase-banner h1.discovery-phase-title {
                    display: none;
                }
                .live-phase-markdown h1.discovery-phase-title {
                    text-align: center;
                    font-size: 2.15rem;
                    font-weight: 700;
                    letter-spacing: -0.02em;
                    margin: 0 0 1.1rem 0;
                    color: rgb(79 70 229);
                }
                .live-phase-markdown h1.discovery-phase-body-h1,
                .live-phase-markdown h1:not(.discovery-phase-title) {
                    font-size: 2.1rem;
                    font-weight: 600;
                    letter-spacing: -0.02em;
                    margin: 0 0 0.95rem 0;
                    color: rgb(15 23 42);
                }
                .live-phase-markdown h2.discovery-toc-label {
                    text-align: left;
                    font-size: 0.95rem;
                    font-weight: 700;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    margin: 2.25rem 0 1rem 0;
                    color: rgb(79 70 229);
                }
                .live-phase-markdown h2.discovery-slot-heading {
                    font-size: 1.35rem;
                    font-weight: 600;
                    margin: 1.35rem 0 0.55rem 0;
                    padding-bottom: 0;
                    color: rgb(30 41 59);
                }
                .live-phase-markdown h2:not(.discovery-toc-label):not(.discovery-slot-heading) {
                    font-size: 1.4rem;
                    font-weight: 600;
                    margin: 1.6rem 0 0.6rem 0;
                    color: rgb(30 41 59);
                }
                .live-phase-markdown h3 {
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin: 1rem 0 0.35rem 0;
                    color: rgb(79 70 229);
                }
                .live-phase-markdown p {
                    margin: 0.5rem 0;
                }
                .live-phase-markdown ol.discovery-toc-ol {
                    list-style: none;
                    padding-left: 0;
                    margin: 0 0 1.25rem 0;
                    counter-reset: discovery-toc-sno;
                }
                .live-phase-markdown ol.discovery-toc-ol li {
                    counter-increment: discovery-toc-sno;
                    position: relative;
                    padding: 0.1rem 0 0.1rem 2.15rem;
                    margin: 0;
                    line-height: 1.4;
                    list-style: none;
                }
                .live-phase-markdown ol.discovery-toc-ol li::before {
                    content: counter(discovery-toc-sno) ".";
                    position: absolute;
                    left: 0;
                    top: 0.1rem;
                    font-weight: 600;
                    font-size: 0.88em;
                    letter-spacing: 0.02em;
                    color: rgb(79 70 229);
                    white-space: nowrap;
                }
                .live-phase-markdown ol.discovery-toc-ol li + li {
                    margin-top: 0;
                }
                .live-phase-markdown ul.discovery-body-ul,
                .live-phase-markdown ul {
                    margin: 0.5rem 0;
                    padding-left: 1.25rem;
                }
                .live-phase-markdown ul.discovery-body-ul li,
                .live-phase-markdown ul li {
                    margin: 0.2rem 0;
                    line-height: 1.45;
                }
                .live-phase-markdown code {
                    background: rgb(241 245 249);
                    border-radius: 0.25rem;
                    padding: 0.1rem 0.35rem;
                    font-size: 0.9em;
                    color: rgb(51 65 85);
                }
                .live-phase-markdown hr {
                    border: none;
                    border-top: 1px solid rgb(226 232 240 / 0.65);
                    margin: 1.5rem 0;
                }
                .live-phase-markdown a {
                    color: rgb(79 70 229);
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }
            `}</style>
        </div>
    );
};

export default LiveDiscoveryDocument;
